import { useEffect, useMemo } from "react";
import {
  TradePriceConfig,
  isLastPriceData,
  isMarkPriceData,
} from "../sdk/types/data";
import { useSubscription, type TradePriceSlice } from "../context/SubscriptionContext";

const EMPTY_SLICE: TradePriceSlice = {
  lastPrice: null,
  markPrice: null,
  displayPrice: null,
  displayTimestamp: null,
  recentTrades: [],
  loading: true,
  error: null,
  isConnected: false,
  priceChange: null,
};

/**
 * Hook for managing last trade price data. Consumes the centralized trade price slice
 * from SubscriptionProvider. No direct WebSocket or HTTP in the hook; one subscription
 * per market shared by all components (TradePrice, TickerBar, TradeOperation).
 */
export function useTradePrice(config: TradePriceConfig) {
  const {
    setTradePriceMarketId,
    tradePriceData,
    refreshTradePrice,
    setBaseURL,
    isConnected: contextConnected,
  } = useSubscription();

  useEffect(() => {
    if (config.baseURL) {
      setBaseURL(config.baseURL);
    }
  }, [config.baseURL, setBaseURL]);

  useEffect(() => {
    if (config.marketId && config.marketId.trim() !== "") {
      setTradePriceMarketId(config.marketId);
    }
  }, [config.marketId, setTradePriceMarketId]);

  const slice = tradePriceData ?? EMPTY_SLICE;
  const lastPrice = slice.lastPrice;
  const markPrice = slice.markPrice;
  const loading = slice.loading;
  const error = slice.error;
  const isConnected = slice.isConnected ?? contextConnected;
  const priceChange = slice.priceChange;
  const recentTrades = slice.recentTrades;
  const displayPrice = slice.displayPrice;
  const displayTimestamp = slice.displayTimestamp;

  const refresh = useMemo(() => refreshTradePrice, [refreshTradePrice]);

  return {
    lastPrice,
    markPrice,
    displayPrice,
    displayTimestamp,
    recentTrades,
    loading,
    error,
    isConnected,
    priceChange,
    refresh,
    infoClient: null,
    isLastPriceData,
    isMarkPriceData,
  };
}
